import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  updateElectricCategory,
  fetchElectricCategories,
  resetElectricCategoryState,
} from "../../../../features/auth/store/electricCategorySlice";
import Modal from "../../../../components/Reusable/Modal";
import { EditProps } from "../types";
import { ElectricCategory } from "../../../../features/auth/store/types/electric-types";

export const EditElectricCategoryModal: React.FC<EditProps> = ({
  id: categoryId,
  isOpen,
  onClose,
}) => {
  const { electricCategories, loading } = useAppSelector(
    (state) => state.electricCategories
  );
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<{
    name: string;
    description: string;
    status: "on" | "off";
  }>({
    name: "",
    description: "",
    status: "on",
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && electricCategories.length === 0) {
      dispatch(fetchElectricCategories());
    }
  }, [dispatch, isOpen, electricCategories.length]);

  useEffect(() => {
    if (!isOpen || categoryId === null) {
      dispatch(resetElectricCategoryState());
      return;
    }

    const categoryToEdit = electricCategories.find(
      (category: ElectricCategory) => category.id === categoryId
    );

    if (categoryToEdit) {
      setForm({
        name: categoryToEdit.name || "",
        description: categoryToEdit.description || "",
        status: categoryToEdit.status || "on",
      });
      setCurrentImageUrl(categoryToEdit.image_url || null);
      setImage(null);
    } else if (!loading && electricCategories.length > 0) {
      console.warn(`Provider with ID ${categoryId} not found in the list.`);
    }
  }, [isOpen, categoryId, electricCategories, loading, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => setImage(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (categoryId === null) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("description", form.description);
      formData.append("status", form.status);
    //   formData.append("_method", "PUT"); // Laravel method spoofing
      
      if (image) {
        formData.append("image", image);
      }
      
      await dispatch(
        updateElectricCategory({ id: categoryId, data: formData as any })
      ).unwrap();
      
      toast.success("Cable Category updated successfully! 🚀");
      onClose();
      // Refresh the list to show updated data
      await dispatch(fetchElectricCategories());
    } catch (err: any) {
      toast.error(err?.message || "Failed to update provider");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-md">
      <h2 className="text-xl font-bold mb-4">Edit Electricity Category</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block mb-2">Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            className="w-full p-2 border rounded"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-2">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-2">Status</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          >
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
        </div>
        <div className="mb-4">
          <label className="block mb-2">Image</label>
          
          {/* Show current image if exists and no new image selected */}
          {currentImageUrl && !image && (
            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-2">Current Image:</p>
              <div className="relative rounded-md overflow-hidden h-20 w-20">
                <img
                  src={currentImageUrl}
                  alt="Current provider"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              id="providerImageUpload"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="providerImageUpload"
              className="flex flex-col items-center justify-center cursor-pointer"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mb-2"
              >
                <path
                  d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                  stroke="#718096"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 8L12 3L7 8"
                  stroke="#718096"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 3V15"
                  stroke="#718096"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-sm text-gray-600">
                {currentImageUrl && !image ? "Upload new image" : "Upload from computer"}
              </span>
            </label>
          </div>
          
          {/* Show new image preview */}
          {image && (
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="relative rounded-md overflow-hidden h-20">
                <img
                  src={URL.createObjectURL(image)}
                  alt="New provider"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-1 right-1 bg-white rounded-full p-1"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="#E53E3E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="#E53E3E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full bg-gradient-to-r from-[#027EC2] to-[#039EF2] h-10 text-white rounded ${
            isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Updating..." : "Update Provider"}
        </button>
      </form>
    </Modal>
  );
};